import { EChartsOption } from 'echarts'
import ReactECharts from 'echarts-for-react'

import { Loading, TrendLabel } from '@/components/customComponents'
import { Card } from '@/components/ui/card'

import { chartColors } from '@/constants/COLOR_PALETTE'
import { PnLMetricCardModel } from '@/types/clarity'
import { formatNumber } from '@/utils'

interface MiniMetricConfig {
  key: keyof PnLMetricCardModel
  header: string
  icon: React.ReactNode
  className?: string
}

interface MasterStackBarChartProps {
  title?: string
  metricCardRecord: Record<string, PnLMetricCardModel>
  miniCardData?: PnLMetricCardModel
  trendData?: PnLMetricCardModel
  loading?: boolean
  miniMetricConfigs: MiniMetricConfig[]
  chartDataConfigs: MiniMetricConfig[]
}

export function MasterStackBarChart({
  title = 'Breakdown',
  metricCardRecord,
  miniCardData,
  trendData,
  loading = false,
  miniMetricConfigs,
  chartDataConfigs,
}: MasterStackBarChartProps) {
  return (
    <Card>
      {/* Card Header */}
      <div className="p-8 pb-0">
        <h3 className="text-2xl">{title}</h3>
      </div>
      {loading ? (
        <div className="mb-8">
          <Loading />
        </div>
      ) : (
        <>
          {/* Mini Metrics */}
          {miniCardData && (
            <MiniMetrics
              miniCardData={miniCardData}
              trendData={trendData}
              miniMetricConfigs={miniMetricConfigs}
            />
          )}
          {/* Chart */}
          <div className="pb-4">
            <ReactECharts
              option={stackedBarChartOptions(
                metricCardRecord,
                chartDataConfigs
              )}
            />
          </div>
        </>
      )}
    </Card>
  )
}

function MiniMetrics({
  miniCardData,
  trendData,
  miniMetricConfigs,
}: {
  miniCardData: PnLMetricCardModel
  trendData?: PnLMetricCardModel
  miniMetricConfigs: MiniMetricConfig[]
}) {
  return (
    <div
      className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 p-8 pb-0`}
    >
      {miniMetricConfigs?.map((config) => (
        <MiniMetricCard
          key={config.key}
          header={config.header}
          metricValue={miniCardData[config.key].value}
          trendValue={trendData?.[config.key].value}
          metricType={miniCardData[config.key].metricType}
          icon={config.icon}
          className={config.className}
        />
      ))}
    </div>
  )
}

function MiniMetricCard({
  header,
  metricValue,
  trendValue,
  metricType,
  icon,
  className,
}: {
  header: string
  metricValue: number
  trendValue?: number
  metricType: 'money' | 'percentage' | 'number' | 'hour'
  icon: React.ReactNode
  className?: string
}) {
  return (
    <div className={`flex ${className || ''}`}>
      <div className="flex">{icon}</div>
      <div className="flex flex-col p-0.5">
        <span className="text-sm text-black-200">{header}</span>
        <div className="flex items-center gap-1">
          <span className="text-xl">
            {formatNumber(metricValue, metricType)}
          </span>
          <TrendLabel
            newValue={metricValue}
            oldValue={trendValue}
            trendType={metricType}
            verySmall={true}
          />
        </div>
      </div>
    </div>
  )
}

const stackedBarChartOptions = (
  data: Record<string, any>,
  chartDataConfigs: MiniMetricConfig[]
): EChartsOption => {
  const xAxisNames = Object.keys(data)

  const series: echarts.BarSeriesOption[] = chartDataConfigs.map(
    (config, index) => {
      const seriesData = xAxisNames.map((dateKey) => {
        const metricCard = data[dateKey]
        return metricCard?.[config.key]?.amount || 0
      })

      return {
        name: config.header,
        type: 'bar',
        stack: 'total',
        barWidth: '60%',
        data: seriesData,
        itemStyle: {
          borderRadius: 8,
          borderWidth: 1,
          borderColor: '#fff',
          color: chartColors[index % chartColors.length],
        },
      }
    }
  )

  return {
    legend: {
      selectedMode: false,
      orient: 'horizontal',
      bottom: '0%',
      left: 'center',
      data: chartDataConfigs.map((config) => config.header),
      icon: 'circle',
      itemGap: 20,
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params: any) {
        let result = params[0].axisValue + '<br/>'
        params.forEach((param: any) => {
          result +=
            param.marker +
            ' ' +
            param.seriesName +
            ': ' +
            formatNumber(param.value, 'money') +
            '<br/>'
        })
        return result
      },
    },
    grid: {
      bottom: '25%',
      left: '12%',
    },
    yAxis: {
      type: 'value',
    },
    center: ['50%', '60%'],
    xAxis: {
      type: 'category',
      data: xAxisNames,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    series,
  }
}
